import React, { type JSX } from 'react';

export type ScanStep<TKey extends string> = { key: TKey; label: string };

type Props<TKey extends string> = {
  steps: ScanStep<TKey>[];
  currentKey: TKey;
};

/** Small breadcrumb showing which scan step is active (e.g. bib -> rear -> front). */
export default function ScanStepBreadcrumb<TKey extends string>(props: Props<TKey>): JSX.Element {
  const { steps, currentKey } = props;
  const currentIndex = steps.findIndex((step) => step.key === currentKey);
  return (
    <div className="flex items-center gap-1.5">
      {steps.map((step, i) => {
        const isCurrent = i === currentIndex;
        const isDone = i < currentIndex;
        return (
          <React.Fragment key={step.key}>
            {i > 0 ? <span className="text-gray-300 dark:text-slate-600">›</span> : null}
            <span
              className={[
                'rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide transition-colors',
                isCurrent
                  ? 'bg-cyan-600 text-white shadow-sm'
                  : isDone
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                    : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500',
              ].join(' ')}
            >
              {step.label}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}
